import { useDocument } from '../context/DocumentContext';
import { buildSignedMediaUrl } from '../services/mediaAccess';

const S3_PREFIX = /^s3:\/\//;

/** What a successfully resolved `resolveHref` result looks like; anything else was never actually resolved. */
const RESOLVED_HREF = /^(?:https?:)?\/\/|^\/|^data:/;

export type ResolvedMediaSrc =
  { status: 'ready'; src: string } | { status: 'loading' } | { status: 'placeholder' };

/**
 * Resolve a figure/image href to a loadable `src`. An `s3://` href can't be
 * signed on its own — it needs the document's media-access grant, held in
 * `DocumentContext` (shared by every figure/inline image, fetched once per
 * submission/revision). Anything already loadable (http(s)/protocol-relative/
 * absolute/data URL) passes through unchanged; anything else (empty,
 * relative, unrecognized) has no way to resolve and falls back to a
 * placeholder. Shared by `FigureImageView` (block figures) and
 * `InlineImageView` (images embedded mid-paragraph).
 */
export function useResolvedMediaSrc(href: string): ResolvedMediaSrc {
  const { contract, mediaAccessQueryString, mediaAccessStatus } = useDocument();

  if (!S3_PREFIX.test(href)) {
    return RESOLVED_HREF.test(href)
      ? { status: 'ready', src: href }
      : { status: 'placeholder' };
  }

  if (mediaAccessStatus === 'error' || mediaAccessStatus === 'unavailable') {
    return { status: 'placeholder' };
  }

  if (mediaAccessStatus !== 'ready' || !mediaAccessQueryString || !contract) {
    return { status: 'loading' };
  }

  const resolvedSrc = buildSignedMediaUrl(
    href,
    contract.submissionId,
    contract.version ?? '',
    contract.revision ?? 0,
    mediaAccessQueryString
  );

  return resolvedSrc ? { status: 'ready', src: resolvedSrc } : { status: 'placeholder' };
}
